import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * A caption-sized help marker that opens its explanation on demand.
 *
 * Long rationale next to a field competes with the field itself; here it stays one click away.
 * Click toggles rather than hover alone, so it also works on touch, and the panel closes on
 * Escape or on a click outside.
 */
const InfoHint = ({ children, label }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const container = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    const onClick = (event) => { if (!container.current?.contains(event.target)) setOpen(false); };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onClick); };
  }, [open]);

  return (
    <span ref={container} className="relative inline-flex align-middle">
      <button
        type="button"
        aria-label={label || t('More information')}
        aria-expanded={open}
        onClick={() => setOpen(value => !value)}
        onMouseEnter={() => setOpen(true)}
        className="flex h-4 w-4 items-center justify-center rounded-full border border-gray-400 text-[10px] font-semibold leading-none text-gray-500 hover:border-blue-500 hover:text-blue-600 dark:border-gray-500 dark:text-gray-400"
      >
        ?
      </button>

      {open && (
        <span
          role="tooltip"
          onMouseLeave={() => setOpen(false)}
          className="absolute left-1/2 top-6 z-30 w-80 -translate-x-1/2 rounded-xl border border-gray-200 bg-white p-3 text-xs font-normal leading-5 text-gray-600 shadow-xl dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
        >
          {children}
        </span>
      )}
    </span>
  );
};

export default InfoHint;
